import React, { useEffect, useState } from 'react';
import EntityDetailDialog from '../../../shared/components/EntityDetailDialog';
import RowActions from '../../../shared/components/patterns/RowActions';
import SupplierDialog from './SupplierDialog';
import SupplierNotesPanel from './SupplierNotesPanel';
import useSupplierOverview from '../hooks/useSupplierOverview';
import '../styles/supplier-detail.css';
import ClientCustomFieldsSection from '../../clients/components/ClientCustomFieldsSection';
import {
  archiveSupplierCustomField,
  createSupplierCustomField,
  listSupplierCustomFields,
} from '../services/suppliers.service';

const SUPPLIER_CUSTOM_FIELDS_API = {
  list: listSupplierCustomFields,
  create: createSupplierCustomField,
  archive: archiveSupplierCustomField,
};

function valueOrEmpty(value) {
  return value || '—';
}

function Field({ label, value }) {
  return (
    <div className="supplier-sheet__field">
      <dt>{label}</dt>
      <dd className="select-text">{valueOrEmpty(value)}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return <section className="supplier-sheet__section" aria-label={title}>
    <h3 className="supplier-sheet__section-title">{title}</h3>
    {children}
  </section>;
}

function Metric({ label, value, description, loading, currency = false }) {
  const displayedValue = value == null ? '—' : currency
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value) : value;
  return <div className="supplier-sheet__metric">
    <span>{label}</span>
    <strong>{loading ? '…' : displayedValue}</strong>
    <p>{loading ? 'Caricamento…' : description || 'Dati non disponibili'}</p>
  </div>;
}

export default function SupplierDetailDialog({ open, supplier, onClose, onSave, canEdit = true, onArchive, onDelete }) {
  const [editing, setEditing] = useState(false);
  const { overview, loading } = useSupplierOverview(supplier, open && !editing);
  useEffect(() => {
    setEditing(false);
  }, [supplier?.id, open]);
  if (!supplier) return null;

  if (editing)
    return (
      <SupplierDialog open={open} supplier={supplier} onSave={onSave} onClose={() => setEditing(false)} />
    );

  const active = supplier.status === 'ACTIVE';
  const title = supplier.displayName || supplier.legalName || 'Fornitore';
  const initial = title.trim().charAt(0).toUpperCase();

  return (
    <EntityDetailDialog
      isOpen={open}
      onClose={onClose}
      title={title}
      subtitle={supplier.category || 'Categoria non indicata'}
      avatarLabel={initial}
      badges={[
        { label: active ? 'Attivo' : 'Inattivo', tone: active ? 'success' : 'muted' },
        { label: supplier.category || 'Tipologia —', tone: 'active' },
        { label: supplier.sector || 'Classificazione —', tone: 'muted' },
      ]}
      editActions={
        canEdit && onSave ? (
          <RowActions
            actions={[
              {
                id: 'edit',
                iconKey: 'actions.edit',
                label: 'Modifica fornitore',
                onClick: () => setEditing(true),
              },
            ]}
          />
        ) : null
      }
      className="supplier-detail-dialog"
      dangerActions={(onArchive || onDelete) ? <RowActions actions={[
        onArchive && { id: 'status', label: active ? 'Disattiva fornitore' : 'Riattiva fornitore', iconKey: active ? 'actions.power' : 'actions.reset', tone: active ? 'danger' : 'default', onClick: () => onArchive(supplier) },
        onDelete && { id: 'delete', label: 'Elimina definitivamente fornitore', iconKey: 'actions.delete', tone: 'danger', onClick: () => onDelete(supplier) },
      ].filter(Boolean)} /> : null}
    >
      <div className="supplier-sheet__layout">
        <Section title="Informazioni aziendali">
          <dl className="supplier-sheet__field-grid">
            <Field label="Ragione sociale" value={supplier.legalName} />
            <Field label="Forma giuridica" value={supplier.legalForm} />
            <Field label="Codice fiscale" value={supplier.taxCode} />
            <Field label="P.IVA" value={supplier.vatNumber} />
            <Field label="Numero REA" value={supplier.reaNumber} />
            <Field label="Settore" value={supplier.sector} />
            <Field label="Categoria" value={supplier.category} />
            <Field label="Nome titolare" value={supplier.ownerName} />
            <Field label="Riferito da" value={supplier.referredBy} />
            <Field label="Indirizzo sede legale" value={supplier.registeredAddress} />
          </dl>
        </Section>

        <Section title="Overview incarichi">
          <div className="supplier-sheet__metrics">
            <Metric label="Uscite collegate" value={overview.expenses?.total} loading={loading}
              description={overview.expenses && `${overview.expenses.paid} pagate · ${overview.expenses.pending} in sospeso`} />
            <Metric label="Da saldare" value={overview.expenses?.outstanding} loading={loading} currency
              description={overview.expenses && `${overview.expenses.pending} scadenze aperte`} />
            <Metric label="Documenti collegati" value={overview.documents?.total} loading={loading}
              description={overview.documents && `${overview.documents.invoices} fatture · ${overview.documents.contracts} contratti${overview.documents.total > overview.documents.invoices + overview.documents.contracts ? ` · ${overview.documents.total - overview.documents.invoices - overview.documents.contracts} altri` : ''}`} />
            <Metric label="Eventi / task" value={overview.activities?.total} loading={loading}
              description={overview.activities && `${overview.activities.open} aperti · ${overview.activities.completed} completati`} />
          </div>
        </Section>

        <Section title="Contatti">
          <div className="supplier-sheet__contacts">
            <div>
              <h4 className="select-text">{supplier.contactName || 'Contatti personali'}</h4>
              {supplier.contactRole ? <p className="supplier-sheet__contact-role select-text">{supplier.contactRole}</p> : null}
              <dl className="supplier-sheet__contact-fields">
                <Field label="Email" value={supplier.contactEmail} />
                <Field label="Telefono" value={supplier.contactPhone} />
              </dl>
            </div>
            <div>
              <h4>Contatti aziendali</h4>
              <dl className="supplier-sheet__contact-fields">
                <Field label="Email" value={supplier.companyEmail} />
                <Field label="Telefono" value={supplier.companyPhone} />
              </dl>
            </div>
          </div>
        </Section>

        <Section title="Descrizione interna">
          <p className="supplier-sheet__description select-text">{supplier.notes || '—'}</p>
        </Section>
        <ClientCustomFieldsSection
          readOnly
          formKey="suppliers.supplier.create"
          values={supplier.customFields || {}}
          api={SUPPLIER_CUSTOM_FIELDS_API}
        />
        <SupplierNotesPanel supplier={supplier} />
      </div>
    </EntityDetailDialog>
  );
}
